import React from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  SaveOutlined,
  UserAddOutlined,
  MailOutlined,
  BankOutlined,
  IdcardOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { isEmail } from "validator";

import { createUsers } from "../../actions/users";
import { genPassword } from "../../utils/password";
import {
  ACCESS_LEVELS,
  PERMISSIONS,
  YES_NO,
  pickPermissions,
} from "../../constants/userAccess";
import BranchSelect from "../../ui/BranchSelect";

const { Title, Text } = Typography;

export default function AddUser() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [generatedPass, setGeneratedPass] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    const password = genPassword(8);

    setGeneratedPass(password);

    form.setFieldsValue({
      firstName: "",
      lastName: "",
      email: "",
      branch: undefined,
      position: "",

      ...PERMISSIONS.reduce((acc, permission) => {
        acc[permission.key] = "Restricted";
        return acc;
      }, {}),

      hr_permission: "no",
      payroll_permission: "no",
    });
  }, [form]);

  const regeneratePassword = () => {
    const password = genPassword(8);

    setGeneratedPass(password);
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPass);
      toast.success("Password copied");
    } catch (error) {
      toast.error("Couldn't copy, please copy it manually");
    }
  };

  const submitAddUser = async () => {
    try {
      const values = await form.validateFields();

      if (!isEmail(values.email)) {
        toast.error("Please enter a valid email address");
        return;
      }

      setSubmitting(true);

      const payload = {
        name: `${values.firstName || ""} ${values.lastName || ""}`.trim(),
        email: values.email,
        password: generatedPass,
        passwordConfirm: generatedPass,

        firstName: values.firstName,
        lastName: values.lastName,
        branch: values.branch,
        position: values.position,

        ...pickPermissions(values),
      };

      await dispatch(createUsers(payload));

      navigate("/admin");
    } catch (error) {
      // Validation errors show on the form; request errors were already
      // toasted by the action, so just stay on the page.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "60px 18px",
        background:
          "linear-gradient(135deg, #eef4ff 0%, #f8fafc 45%, #eefdf5 100%)",
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
        }}
      >
        {/* Top header */}
        <div
          style={{
            marginBottom: 18,
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Space size={14}>
            <div
              style={{
                width: 54,
                height: 54,
                borderRadius: 18,
                background: "linear-gradient(135deg, #1677ff, #4096ff)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 12px 28px rgba(22, 119, 255, .28)",
              }}
            >
              <UserAddOutlined style={{ color: "#fff", fontSize: 26 }} />
            </div>

            <div>
              <Title level={3} style={{ margin: 0, fontWeight: 900 }}>
                Add New User
              </Title>
              <Text type="secondary">
                Create login credentials and assign system permissions.
              </Text>
            </div>
          </Space>

          <Button
            size="large"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/admin")}
            style={{
              borderRadius: 12,
              fontWeight: 700,
            }}
          >
            Back to Users
          </Button>
        </div>

        <Row gutter={[18, 18]}>
          {/* Main form */}
          <Col xs={24} lg={17}>
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
                boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
                overflow: "hidden",
              }}
              bodyStyle={{ padding: 0 }}
            >
              <div
                style={{
                  padding: "18px 22px",
                  background:
                    "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                  color: "#fff",
                }}
              >
                <Space>
                  <IdcardOutlined style={{ fontSize: 20 }} />
                  <div>
                    <Text style={{ color: "#dbeafe", fontWeight: 800 }}>
                      User Information
                    </Text>
                    <div style={{ color: "#94a3b8", fontSize: 12 }}>
                      Enter the user’s identity and work details.
                    </div>
                  </div>
                </Space>
              </div>

              <div style={{ padding: 22 }}>
                <Form layout="vertical" form={form}>
                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="firstName"
                        label="First name"
                        rules={[
                          {
                            required: true,
                            message: "First name is required",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Enter first name"
                          style={{ borderRadius: 12 }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="lastName"
                        label="Last name"
                        rules={[
                          {
                            required: true,
                            message: "Last name is required",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Enter last name"
                          style={{ borderRadius: 12 }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="email"
                        label="User email"
                        rules={[
                          {
                            required: true,
                            message: "Email is required",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          prefix={<MailOutlined />}
                          placeholder="example@email.com"
                          style={{ borderRadius: 12 }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="branch"
                        label="User branch"
                        rules={[
                          {
                            required: true,
                            message: "Branch is required",
                          },
                        ]}
                      >
                        <BranchSelect
                          size="large"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Form.Item
                        name="position"
                        label="Position"
                        rules={[
                          {
                            required: true,
                            message: "Position is required",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          prefix={<BankOutlined />}
                          placeholder="Enter user position"
                          style={{ borderRadius: 12 }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Divider style={{ margin: "10px 0 18px" }} />

                      <Space>
                        <LockOutlined style={{ color: "#1677ff" }} />
                        <Title level={5} style={{ margin: 0 }}>
                          Login Password
                        </Title>
                      </Space>
                    </Col>

                    <Col xs={24}>
                      <div
                        style={{
                          padding: 14,
                          borderRadius: 16,
                          background: "#f1f5f9",
                          border: "1px solid #e2e8f0",
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <Space wrap>
                          <Text strong>Generated password:</Text>
                          <Tag
                            color="blue"
                            style={{
                              padding: "4px 12px",
                              borderRadius: 999,
                              fontWeight: 800,
                              letterSpacing: 1,
                              fontFamily: "monospace",
                              fontSize: 14,
                            }}
                          >
                            {generatedPass}
                          </Tag>
                        </Space>

                        <Space wrap>
                          <Button
                            icon={<CopyOutlined />}
                            onClick={copyPassword}
                            style={{
                              borderRadius: 12,
                              fontWeight: 700,
                            }}
                          >
                            Copy
                          </Button>

                          <Button
                            icon={<ReloadOutlined />}
                            onClick={regeneratePassword}
                            style={{
                              borderRadius: 12,
                              fontWeight: 700,
                            }}
                          >
                            Regenerate
                          </Button>
                        </Space>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Card>
          </Col>

          {/* Side summary card */}
          <Col xs={24} lg={7}>
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
                boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              }}
              bodyStyle={{ padding: 22 }}
            >
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <div>
                  <Tag
                    color="green"
                    style={{
                      borderRadius: 999,
                      padding: "4px 12px",
                      fontWeight: 800,
                    }}
                  >
                    New Account Setup
                  </Tag>

                  <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
                    Security Note
                  </Title>

                  <Text type="secondary">
                    The password is generated automatically and emailed to the
                    user. Copy it before saving if you need to hand it over, and
                    advise them to change it after first login.
                  </Text>
                </div>

                <Divider style={{ margin: 0 }} />

                <div
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <Text strong style={{ color: "#15803d" }}>
                    Permissions
                  </Text>
                  <div style={{ marginTop: 6 }}>
                    <Text type="secondary">
                      All permissions default to Restricted until changed.
                    </Text>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>

          {/* Permissions */}
          <Col xs={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
                boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
              }}
              bodyStyle={{ padding: 22 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: 18,
                }}
              >
                <Space>
                  <SafetyCertificateOutlined
                    style={{ fontSize: 22, color: "#1677ff" }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      Platform Permissions
                    </Title>
                    <Text type="secondary">
                      Assign access levels for each platform.
                    </Text>
                  </div>
                </Space>
              </div>

              <Form layout="vertical" form={form}>
                <Row gutter={[16, 16]}>
                  {PERMISSIONS.map((permission) => (
                    <Col key={permission.key} xs={24} sm={12} md={8} lg={6}>
                      <div
                        style={{
                          padding: 14,
                          borderRadius: 18,
                          border: "1px solid #e5e7eb",
                          background:
                            "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                        }}
                      >
                        <Form.Item
                          name={permission.key}
                          label={
                            <Text strong style={{ color: "#334155" }}>
                              {permission.label}
                            </Text>
                          }
                          initialValue="Restricted"
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            size="large"
                            style={{ width: "100%" }}
                            options={ACCESS_LEVELS.map((level) => ({
                              label: level,
                              value: level,
                            }))}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  ))}

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div
                      style={{
                        padding: 14,
                        borderRadius: 18,
                        border: "1px solid #e5e7eb",
                        background:
                          "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      }}
                    >
                      <Form.Item
                        name="hr_permission"
                        label={
                          <Text strong style={{ color: "#334155" }}>
                            HR Right
                          </Text>
                        }
                        initialValue="no"
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          size="large"
                          options={YES_NO.map((x) => ({
                            label: x.toUpperCase(),
                            value: x,
                          }))}
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div
                      style={{
                        padding: 14,
                        borderRadius: 18,
                        border: "1px solid #e5e7eb",
                        background:
                          "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      }}
                    >
                      <Form.Item
                        name="payroll_permission"
                        label={
                          <Text strong style={{ color: "#334155" }}>
                            Payroll Right
                          </Text>
                        }
                        initialValue="no"
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          size="large"
                          options={YES_NO.map((x) => ({
                            label: x.toUpperCase(),
                            value: x,
                          }))}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>

          {/* Actions */}
          <Col xs={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
                boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
              }}
              bodyStyle={{
                padding: 18,
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <Button
                size="large"
                onClick={() => navigate("/admin")}
                style={{
                  borderRadius: 12,
                  fontWeight: 700,
                }}
              >
                Cancel
              </Button>

              <Button
                size="large"
                type="primary"
                icon={<SaveOutlined />}
                loading={submitting}
                onClick={submitAddUser}
                style={{
                  borderRadius: 12,
                  fontWeight: 800,
                  boxShadow: "0 10px 20px rgba(22, 119, 255, .25)",
                }}
              >
                Create User
              </Button>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
